import * as React from 'react';
import { useState } from 'react';
import { NewWorkspaceModalView, NewWorkspaceModalViewProps } from './NewWorkspaceModal.view';
import { ApolloError } from '@apollo/client';
import {
  CreateWorkspaceMutationFn,
  TimezoneSetting,
  UpdateWorkspaceMutationFn,
  UserOrganizationRole,
  WorkspaceInput
} from '../../../models/graphql';
import { RegisterOptions, useForm, UseFormReturn } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { useUserData } from '../../../hooks/useUserData';
import { ValidationMessages } from '../../../constants/validation-messages';
import { TimezoneMap, TimezoneNames } from '../../../constants/timezones';
import { convertTimezoneNameToEnum } from '../../../helpers/timeFormatter';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { showToast } from '../../../helpers/showToast';
import { SubmitHandler } from 'react-hook-form/dist/types/form';

export type NewWorkspaceModalControllerProps = Omit<
  NewWorkspaceModalViewProps,
  'onSubmit' | 'submitted' | 'nameRules' | 'timezoneRules' | 'noRules' | 'selectedTimezone' | 'useFormInstance'
> & {
  createWorkspace: CreateWorkspaceMutationFn;
  updateWorkspace: UpdateWorkspaceMutationFn;
};

export const NewWorkspaceModalController = (props: NewWorkspaceModalControllerProps): JSX.Element => {
  const userData = useUserData();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const { selectedOrganization } = useSelectedOrganization();
  const defaultFormValues: Record<string, string | boolean> = {
    workspaceId: ''
  };

  if (!props.workspace) {
    props.members?.forEach(member => {
      // Only check if They are already assigned (for edit) or
      // If the member is a Owner/Admin
      if ([UserOrganizationRole.OWNER, UserOrganizationRole.ADMIN].includes(member.role)) {
        defaultFormValues[`${member.status}[${member.id}]`] = true;
      }
    });
  } else {
    const workspaceId = props.workspace.id;
    props.members?.forEach(member => {
      if (member.workspaces?.includes(workspaceId) || member.role !== UserOrganizationRole.EDITOR) {
        defaultFormValues[`${member.status}[${member.id}]`] = true;
      }
    });
  }

  let selectedTimezone = props.workspace
    ? convertTimezoneNameToEnum(TimezoneMap[props.workspace.timezone] as TimezoneNames)
    : convertTimezoneNameToEnum(Intl.DateTimeFormat().resolvedOptions().timeZone as TimezoneNames);

  // Always have a default timezone
  selectedTimezone = selectedTimezone || TimezoneSetting.UTC;
  defaultFormValues['timezone'] = selectedTimezone;

  if (props.workspace) {
    defaultFormValues['workspaceId'] = props.workspace.id;
    defaultFormValues['name'] = props.workspace.name;
  }

  const useFormInstance: UseFormReturn = useForm({
    ...FormOptions,
    defaultValues: defaultFormValues
  });

  const noRules = {};
  const timezoneRules: RegisterOptions = {
    required: ValidationMessages.timezone.required
  };
  const nameRules: RegisterOptions = {
    required: ValidationMessages.workspace.name.required,
    maxLength: {
      value: 200,
      message: ValidationMessages.workspace.name.maxLength
    },
    minLength: {
      value: 2,
      message: ValidationMessages.workspace.name.minLength
    }
  };

  const onSubmit: SubmitHandler<{
    workspaceId: string;
    name: string;
    timezone: TimezoneSetting;
    member: [];
    invited: [];
  }> = (data, ev) => {
    ev?.preventDefault();
    setSubmitted(true);

    const assignedUsers: string[] = [];
    const assignedInvitees: string[] = [];

    // USERS/MEMBERS
    for (const [key, value] of Object.entries(data.member)) {
      if (value !== false) {
        assignedUsers.push(key);
      }
    }

    // INVITED
    if (data.invited) {
      for (const [key, value] of Object.entries(data.invited)) {
        if (value) {
          assignedInvitees.push(key);
        }
      }
    }

    const input: WorkspaceInput = {
      name: data.name,
      timezone: data.timezone,
      userIds: assignedUsers, // data.member,
      userInvitationIds: assignedInvitees // data.invited
    };

    if (selectedOrganization && props.workspace) {
      // WHEN EDITING EXISTING WORKSPACE
      props
        .updateWorkspace({
          variables: {
            workspaceId: props.workspace.id,
            input: input
          }
        })
        .then(async () => {
          await userData.refetch().catch(() => {
            // todo toast
          }); // for Org/Workspace switcher
          props.onClose();
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in updating your workspace',
            detail: mappedError,
            severity: 'error'
          });
          setSubmitted(false);
        });
    } else if (selectedOrganization) {
      // CREATING NEW WORKSPACES
      props
        .createWorkspace({
          variables: {
            organizationId: selectedOrganization.id,
            input: input
          }
        })
        .then(async () => {
          await userData.refetch().catch(() => {
            // todo toast
          }); // for Org/Workspace switcher
          props.onClose();
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in creating your workspace',
            detail: mappedError,
            severity: 'error'
          });
          setSubmitted(false);
        });
    }
  };

  const viewProps: NewWorkspaceModalViewProps = {
    ...props,
    submitted,
    onSubmit,
    useFormInstance,
    nameRules,
    selectedTimezone,
    timezoneRules,
    noRules
  };

  return <NewWorkspaceModalView {...viewProps} />;
};
